import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { PanelLeft } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/cn';
import { Button } from './button';
import { Sheet, SheetContent } from './sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

/**
 * Slim shadcn-style Sidebar primitive.
 *
 * Desktop: 240px expanded / 56px collapsed icon-only rail. Cmd+B toggles.
 * State persists in localStorage so the choice survives navigation.
 *
 * Mobile (< lg = 1024px): the sidebar lives inside a Sheet overlay
 * triggered by SidebarTrigger in the topbar.
 *
 * Compose as:
 *   <SidebarProvider>
 *     <Sidebar>
 *       <SidebarHeader>…</SidebarHeader>
 *       <SidebarContent>
 *         <SidebarGroup>
 *           <SidebarGroupLabel>Section</SidebarGroupLabel>
 *           <SidebarGroupContent>
 *             <SidebarMenu>
 *               <SidebarMenuItem>
 *                 <SidebarMenuButton tooltip="Pulpit" isActive={…}>
 *                   …icon… label
 *                 </SidebarMenuButton>
 *               </SidebarMenuItem>
 *             </SidebarMenu>
 *           </SidebarGroupContent>
 *         </SidebarGroup>
 *       </SidebarContent>
 *     </Sidebar>
 *     <SidebarInset>{routerOutlet}</SidebarInset>
 *   </SidebarProvider>
 */

const SIDEBAR_WIDTH = '15rem';            // 240px expanded
const SIDEBAR_WIDTH_ICON = '3.5rem';      // 56px collapsed (icon-only)
const SIDEBAR_STATE_KEY = 'h2m_sidebar_state_v1';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';    // Cmd+B / Ctrl+B

type SidebarState = 'expanded' | 'collapsed';

interface SidebarContextValue {
  state: SidebarState;
  open: boolean;          // alias for state === 'expanded'
  setOpen: (next: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (next: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
}

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

export function useSidebar() {
  const ctx = React.useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebar must be used within a <SidebarProvider>');
  return ctx;
}

function useIsMobile(breakpointPx = 1024) {
  const [mobile, setMobile] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(max-width: ${breakpointPx - 1}px)`).matches;
  });
  React.useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpointPx - 1}px)`);
    const onChange = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [breakpointPx]);
  return mobile;
}

export interface SidebarProviderProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultOpen?: boolean;
}

export const SidebarProvider = React.forwardRef<HTMLDivElement, SidebarProviderProps>(
  ({ defaultOpen = true, className, style, children, ...props }, ref) => {
    const isMobile = useIsMobile();

    // Lazy-init from localStorage so the first paint matches the saved choice.
    const [open, setOpenInternal] = React.useState<boolean>(() => {
      if (typeof window === 'undefined') return defaultOpen;
      try {
        const saved = window.localStorage.getItem(SIDEBAR_STATE_KEY);
        if (saved === 'expanded') return true;
        if (saved === 'collapsed') return false;
      } catch {
        /* localStorage may be unavailable (private mode) */
      }
      return defaultOpen;
    });

    const setOpen = React.useCallback((next: boolean) => {
      setOpenInternal(next);
      try {
        window.localStorage.setItem(SIDEBAR_STATE_KEY, next ? 'expanded' : 'collapsed');
      } catch {
        /* noop */
      }
    }, []);

    const [openMobile, setOpenMobile] = React.useState(false);

    const toggleSidebar = React.useCallback(() => {
      if (isMobile) {
        setOpenMobile((v) => !v);
      } else {
        setOpen(!open);
      }
    }, [isMobile, open, setOpen]);

    // Cmd+B / Ctrl+B keyboard shortcut.
    React.useEffect(() => {
      const onKey = (e: KeyboardEvent) => {
        if (e.key === SIDEBAR_KEYBOARD_SHORTCUT && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          toggleSidebar();
        }
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }, [toggleSidebar]);

    const state: SidebarState = open ? 'expanded' : 'collapsed';

    const value = React.useMemo<SidebarContextValue>(
      () => ({ state, open, setOpen, openMobile, setOpenMobile, isMobile, toggleSidebar }),
      [state, open, setOpen, openMobile, isMobile, toggleSidebar]
    );

    return (
      <SidebarContext.Provider value={value}>
        <div
          ref={ref}
          data-sidebar-state={state}
          style={
            {
              ['--sidebar-width' as string]: SIDEBAR_WIDTH,
              ['--sidebar-width-icon' as string]: SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={cn('flex h-svh w-full bg-surface', className)}
          {...props}
        >
          {children}
        </div>
      </SidebarContext.Provider>
    );
  }
);
SidebarProvider.displayName = 'SidebarProvider';

/**
 * Sidebar — the actual rail. Renders inline on desktop, inside a Sheet
 * on mobile. Children should be SidebarHeader / SidebarContent /
 * SidebarFooter (in that order).
 */
export const Sidebar = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ className, children, ...props }, ref) => {
    const { state, isMobile, openMobile, setOpenMobile } = useSidebar();

    if (isMobile) {
      return (
        <Sheet open={openMobile} onOpenChange={setOpenMobile}>
          <SheetContent
            side="left"
            className="w-[var(--sidebar-width)] !max-w-none bg-surface p-0"
          >
            <aside ref={ref} className={cn('flex h-full flex-col', className)} {...props}>
              {children}
            </aside>
          </SheetContent>
        </Sheet>
      );
    }

    return (
      <aside
        ref={ref}
        data-state={state}
        className={cn(
          'group/sidebar relative hidden h-svh flex-col border-r border-border bg-surface transition-[width] duration-200 ease-out lg:flex',
          state === 'expanded' ? 'w-[var(--sidebar-width)]' : 'w-[var(--sidebar-width-icon)]',
          className
        )}
        {...props}
      >
        {children}
      </aside>
    );
  }
);
Sidebar.displayName = 'Sidebar';

export const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar-header
    className={cn('flex flex-col gap-2 border-b border-border p-3', className)}
    {...props}
  />
));
SidebarHeader.displayName = 'SidebarHeader';

export const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar-footer
    className={cn('mt-auto flex flex-col gap-2 border-t border-border p-3', className)}
    {...props}
  />
));
SidebarFooter.displayName = 'SidebarFooter';

export const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar-content
    className={cn(
      'flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden py-2',
      'scrollbar-thin',
      className
    )}
    {...props}
  />
));
SidebarContent.displayName = 'SidebarContent';

export const SidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar-group
    className={cn('relative flex w-full min-w-0 flex-col px-2 py-1', className)}
    {...props}
  />
));
SidebarGroup.displayName = 'SidebarGroup';

export const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar-group-label
    className={cn(
      'flex h-7 shrink-0 items-center px-2 text-xs font-medium uppercase tracking-wide text-text-subtle',
      'group-data-[state=collapsed]/sidebar:opacity-0 group-data-[state=collapsed]/sidebar:pointer-events-none',
      'transition-opacity duration-150',
      className
    )}
    {...props}
  />
));
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

export const SidebarGroupContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('mt-1 w-full', className)} {...props} />
));
SidebarGroupContent.displayName = 'SidebarGroupContent';

export const SidebarMenu = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    data-sidebar-menu
    className={cn('flex w-full min-w-0 flex-col gap-0.5', className)}
    {...props}
  />
));
SidebarMenu.displayName = 'SidebarMenu';

export const SidebarMenuItem = React.forwardRef<
  HTMLLIElement,
  React.HTMLAttributes<HTMLLIElement>
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('group/menu-item relative', className)} {...props} />
));
SidebarMenuItem.displayName = 'SidebarMenuItem';

const sidebarMenuButtonVariants = cva(
  'peer/menu-button flex w-full items-center gap-3 overflow-hidden rounded-md px-3 py-2 text-left text-sm font-medium outline-none ring-accent transition-[width,height,padding,background-color] focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      isActive: {
        true: 'bg-bg text-text shadow-[0_1px_2px_rgba(0,0,0,0.04)] ring-1 ring-black/5',
        false: 'text-text-muted hover:bg-bg/60 hover:text-text',
      },
    },
    defaultVariants: { isActive: false },
  }
);

export interface SidebarMenuButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof sidebarMenuButtonVariants> {
  asChild?: boolean;
  tooltip?: string;
}

/**
 * SidebarMenuButton — primary nav button. When the sidebar is collapsed,
 * the text label is hidden and a Tooltip with the `tooltip` prop fires
 * on hover. Use `asChild` to render as a router `<Link>` while keeping
 * the button styling.
 */
export const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(
  ({ asChild, isActive, tooltip, className, children, ...props }, ref) => {
    const { state, isMobile } = useSidebar();
    const Comp = asChild ? Slot : 'button';

    const button = (
      <Comp
        ref={ref}
        data-active={isActive ? 'true' : undefined}
        className={cn(sidebarMenuButtonVariants({ isActive }), className)}
        {...props}
      >
        {children}
      </Comp>
    );

    // Tooltip only when collapsed AND not on mobile (mobile sheet always
    // shows full labels). The tooltip text mirrors the label for screen
    // readers + hover discovery.
    if (!tooltip || state === 'expanded' || isMobile) return button;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="right" align="center">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    );
  }
);
SidebarMenuButton.displayName = 'SidebarMenuButton';

/**
 * SidebarTrigger — button to toggle the sidebar. Put in the topbar.
 * Shows different affordance on mobile (opens sheet) vs desktop
 * (collapses to icon-only rail). Cmd+B / Ctrl+B keyboard shortcut works
 * regardless.
 */
export const SidebarTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();
  return (
    <Button
      ref={ref}
      type="button"
      variant="ghost"
      size="icon"
      aria-label="Przełącz pasek boczny"
      className={cn('size-9', className)}
      onClick={(e) => {
        onClick?.(e);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeft className="size-4" aria-hidden="true" />
    </Button>
  );
});
SidebarTrigger.displayName = 'SidebarTrigger';

/**
 * SidebarInset — the main content area to the right of the sidebar.
 * Takes full remaining width. The topbar + page outlet go inside.
 */
export const SidebarInset = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ className, ...props }, ref) => (
    <main
      ref={ref}
      className={cn(
        'relative flex h-svh min-w-0 flex-1 flex-col bg-surface',
        className
      )}
      {...props}
    />
  )
);
SidebarInset.displayName = 'SidebarInset';
